import { createFurnitureElement } from "../../../../utils/shapeFactory";
import { Box, ClipboardPaste, Smile, Sofa, Type, Upload } from 'lucide-react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FurnitureSelectionModal } from '../../../FurnitureSelectionModal';
import { IconSelectionModal } from '../../../IconSelectionModal';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger
} from "../../../ui/context-menu";
import {
  useMapEditorActions,
  useMapEditorUI
} from "../../contexts/MapEditorContext";
import { getStagePositionStartingFromPoint } from "../../helpers/getStagePositionStartingFromPoint";
import { getWindowKonvaObject } from "../../helpers/konva/getWindowKonvaObject";
import { useShapeMetrics } from "../../helpers/shapeMetrics";
import { MapPosition } from "../../types";
import { useUploadFloorPlan } from "../helpers/uploadFloorPlan";
import { ShapeContextMenuItems } from "./ShapeContextMenuItems";

export const MapEditorContextMenu: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [clickPosition, setClickPosition] = useState<MapPosition | null>(null);
  const { contextMenuShape, clipboard } = useMapEditorUI();
  const {
    setContextMenuShape,
    addText,
    addCustomMultiShape,
    addSvgFromFile,
    addIconSvg,
    pasteShapes,
    setShapes,
    setSelectedIds
  } = useMapEditorActions();

  const uploadFloorPlan = useUploadFloorPlan();

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setContextMenuShape(null);
    }
  };

  const metrics = useShapeMetrics();

  const handleContextMenu = (e: React.MouseEvent) => {
    const pointerPosition = metrics
      ? metrics.getPointerPosition()
      : getWindowKonvaObject().stages[0]?.getPointerPosition();
    if (!pointerPosition) return;

    setClickPosition({ x: pointerPosition.x, y: pointerPosition.y });
  };

  const [lastClickPosition, setLastClickPosition] =
    useState<MapPosition | null>(null);

  const handleAction = (action: (position: MapPosition) => void) => {
    if (!clickPosition) return;

    const position = metrics
      ? metrics.toWorld(clickPosition)
      : getStagePositionStartingFromPoint(clickPosition);
    if (!position) return;

    setLastClickPosition(position);
    action(position);

    setClickPosition(null);
    setContextMenuShape(null);
  };

  const [isIconModalOpen, setIsIconModalOpen] = useState(false);
  const [isFurnitureModalOpen, setIsFurnitureModalOpen] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      await uploadFloorPlan(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast.error("Error uploading floor plan");
    }

    setClickPosition(null);
    setContextMenuShape(null);
  };

  const pasteClipboard = (position: MapPosition) => {
    pasteShapes({
      shapeOverrides: {
        ...position
      }
    });
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".svg"
        onChange={handleFileChange}
        className="hidden"
      />
      <ContextMenu onOpenChange={handleOpenChange}>
        <ContextMenuTrigger onContextMenu={handleContextMenu} asChild>
          <div className="h-full w-full">{children}</div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          {contextMenuShape ? (
            <ShapeContextMenuItems
              onPasteHere={() => handleAction(pasteClipboard)}
            />
          ) : (
            <>
              <ContextMenuItem
                disabled={!clipboard?.shapes?.length}
                onSelect={() => handleAction(pasteClipboard)}
              >
                <ClipboardPaste className="mr-2 h-4 w-4" />
                Paste here
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem onSelect={() => handleAction(addText)}>
                <Type className="mr-2 h-4 w-4 text-blue-500" />
                Add Text
              </ContextMenuItem>
              <ContextMenuItem
                onSelect={() =>
                  handleAction(() => setIsFurnitureModalOpen(true))
                }
              >
                <Sofa className="mr-2 h-4 w-4 text-blue-500" />
                Add Furniture
              </ContextMenuItem>
              <ContextMenuItem
                onSelect={() => handleAction(() => setIsIconModalOpen(true))}
              >
                <Smile className="mr-2 h-4 w-4 text-blue-500" />
                Add Icon
              </ContextMenuItem>
              <ContextMenuItem
                onSelect={() => handleAction(addCustomMultiShape)}
              >
                <Box className="mr-2 h-4 w-4 text-blue-500" />
                Add Resource
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem onSelect={() => fileInputRef.current?.click()}>
                <Upload className="mr-2 h-4 w-4 text-green-500" />
                Upload Floor Plan
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>
      <IconSelectionModal
        open={isIconModalOpen}
        onClose={() => setIsIconModalOpen(false)}
        onSelect={(Icon) => addIconSvg(Icon, lastClickPosition || undefined)}
      />

      <FurnitureSelectionModal
        open={isFurnitureModalOpen}
        onClose={() => setIsFurnitureModalOpen(false)}
        onSelect={(furniture) => {
          if (furniture.dynamicTable) {
            const shape = createFurnitureElement(lastClickPosition || undefined, furniture);
            setShapes((all) => [...all, shape]);
            setSelectedIds([shape.id]);
            return;
          }
          // Built-in furniture carries explicit width/height in
          // FURNITURE_LIST. Uploaded furniture often has none — its
          // SVG's viewBox can be 1000+ units, which lands as a giant
          // shape on the map even though the picker thumbnail is tiny.
          // Pass through explicit dimensions when available so the
          // placed shape matches what the user saw in the picker.
          const shape: Partial<typeof furniture> & {
            x?: number;
            y?: number;
          } = { ...lastClickPosition };
          if (furniture.width) shape.width = furniture.width;
          if (furniture.height) shape.height = furniture.height;
          addSvgFromFile(furniture.url, { shape }).then();
        }}
      />
    </>
  );
};
